import React, { useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
} from '../../../components/ui';
import { buildYearOptions, currentYear, formatReportDate } from './reportYearUtils';

/**
 * Standard report page header with calendar year selection.
 */
const ReportPageHeader = ({
  title,
  description,
  lastUpdated,
  year,
  yearOptions,
  onYearChange,
  onRefresh,
  theme,
  dash,
  cn,
  cardClassName,
}) => {
  const years = useMemo(() => {
    if (Array.isArray(yearOptions) && yearOptions.length > 0) {
      return yearOptions.map((y) => Number(y)).filter((y) => Number.isFinite(y));
    }
    return buildYearOptions();
  }, [yearOptions]);

  const selectedYear = years.includes(Number(year)) ? Number(year) : currentYear();

  return (
    <Card className={cardClassName}>
      <div className={cn('h-1 w-full rounded-t-xl', theme.gradients.header)} />
      <CardHeader className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 pt-5">
        <div className="min-w-0">
          <CardTitle className="text-2xl sm:text-3xl">{title}</CardTitle>
          {description ? (
            <CardDescription className="mt-1 text-base">{description}</CardDescription>
          ) : null}
          {lastUpdated ? (
            <p className={cn('text-xs mt-2 tabular-nums', theme.text.muted)}>
              Last updated:{' '}
              {lastUpdated instanceof Date
                ? `${formatReportDate(lastUpdated)} ${lastUpdated.toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                  })}`
                : String(lastUpdated)}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0 relative z-20">
          {onYearChange ? (
            <label className="flex items-center gap-2">
              <span className={cn('text-xs font-semibold uppercase tracking-wide', theme.text.muted)}>
                Year
              </span>
              <select
                value={String(selectedYear)}
                onChange={(e) => onYearChange(e.target.value)}
                className={cn(
                  'h-10 min-w-[7rem] rounded-md border px-3 text-sm',
                  theme.colors.surface,
                  theme.text.primary,
                  theme.border.light,
                  'focus:outline-none focus:ring-2 focus:ring-blue-500/20',
                )}
              >
                {years.map((y) => (
                  <option key={y} value={String(y)}>
                    {y}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {onRefresh ? (
            <Button type="button" variant="secondary" onClick={onRefresh}>
              Refresh
            </Button>
          ) : null}
        </div>
      </CardHeader>
      <div className={cn('px-6 pb-4 flex items-center gap-2 text-xs border-t', theme.border.light)}>
        <span className={cn('w-2 h-2 rounded-full', dash.dot('success'))} />
        <span className={theme.text.muted}>Live data from QMS · Year {selectedYear}</span>
      </div>
    </Card>
  );
};

export default ReportPageHeader;
